/**
 * Authentication Context
 * Manages JWT tokens and user authentication state
 */

import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { auth as authAPI } from '../services/api';

const AuthContext = createContext(null);
// Unverified decoding is used ONLY to refuse a URL transport for this purpose,
// never to authenticate a user or grant a role. Ordinary D2L URLs are unchanged.
function isCanaryToken(value) {
  try {
    const payload = JSON.parse(window.atob(value.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.authMethod === 'canary-operator' || payload.tokenPurpose !== undefined || payload.canaryScope !== undefined;
  } catch { return false; }
}

// A staff member testing their lab through its D2L link ("Test the D2L link")
// signs THIS tab in as their test student. A staff sign-in already in the
// browser stays where it is, so the dashboard's other tabs keep working and the
// IDE (which reads the stored token) still knows its owner. Unverified decoding
// only chooses where a token is kept; the backend decides what it may do.
export const TAB_TOKEN_KEY = 'testStudentToken';
function claims(value) {
  try { return JSON.parse(window.atob(String(value).split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); } catch { return null; }
}
function keepsStaffSignIn(newToken) {
  return claims(newToken)?.test === true && ['faculty', 'admin'].includes(claims(localStorage.getItem('token'))?.role);
}
const tabToken = () => { try { return sessionStorage.getItem(TAB_TOKEN_KEY); } catch { return null; } };
const setTabToken = (value) => {
  try { if (value) sessionStorage.setItem(TAB_TOKEN_KEY, value); else sessionStorage.removeItem(TAB_TOKEN_KEY); } catch { /* storage unavailable */ }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // New D2L launches carry the token in the fragment (never sent in an HTTP
  // request or Referer). Still accept the old ?token= handoff for saved links.
  // /open-app and /open-preview use an opaque #host/path?query fragment: an
  // address on a student's own origin. An app's own &token= parameter, or a
  // page's, must never be mistaken for a dashboard sign-in, so those two pages
  // never take a token from the URL at all.
  const bridge = /^\/open-(app|preview)\/?$/.test(location.pathname);
  const urlToken = bridge ? null : ((location.hash.startsWith('#token=')
    ? new URLSearchParams(location.hash.slice(1)).get('token') : null)
    || new URLSearchParams(location.search).get('token'));
  const incomingToken = urlToken && !isCanaryToken(urlToken) ? urlToken : null;
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => incomingToken || tabToken() || localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const currentToken = useRef(token);
  const canaryState = useRef(null);

  const applyBodyToken = useCallback((newToken, userData = null) => {
    if (keepsStaffSignIn(newToken)) setTabToken(newToken);
    else {
      setTabToken(null);
      localStorage.setItem('token', newToken);
    }
    setAuthError(null);
    // Receiving the same handoff twice must not discard a loaded profile or
    // leave loading=true without a new profile request.
    if (currentToken.current === newToken) return;
    currentToken.current = newToken;
    queryClient.clear();
    setToken(newToken);
    setUser(userData);
    setLoading(!userData);
  }, [queryClient]);
  const loginWithToken = useCallback((newToken, userData = null) => {
    // Ordinary OTP/D2L callers keep this existing API. Canary tokens may only
    // arrive through the private, backend-response exchange below.
    if (isCanaryToken(newToken)) throw new Error('Use the scoped body exchange');
    applyBodyToken(newToken, userData);
  }, [applyBodyToken]);
  const beginCanarySignIn = useCallback(async (studentId) => {
    if (location.pathname !== '/student-login' || location.search || location.hash || !/^five00[1-5]$/.test(studentId)) throw new Error('Scoped sign-in refused');
    const state = await authAPI.beginCanarySignIn(studentId);
    canaryState.current = { ...state, studentId };
    return state;
  }, [location.pathname, location.search, location.hash]);
  const completeCanarySignIn = useCallback(async (authorization) => {
    const state = canaryState.current; canaryState.current = null;
    if (!state || location.pathname !== '/student-login' || location.search || location.hash) throw new Error('Scoped sign-in refused');
    const result = await authAPI.exchangeCanarySignIn(authorization);
    if (result.authMethod !== 'canary-operator' || result.scope?.purpose !== 'infs4102-five-canary-operator-signin'
      || result.scope.studentId !== state.studentId || result.scope.browser?.stateId !== state.stateId
      || result.scope.browser?.origin !== window.location.origin || result.redirectPath !== state.redirectPath
      || !/^\/launch\/[a-f0-9]{24}$/.test(result.redirectPath) || typeof result.token !== 'string' || !isCanaryToken(result.token)
      || result.user?.role !== 'student' || result.user.id !== result.scope.userId || result.user.studentId !== state.studentId
      || result.expiresAtMs <= Date.now() || result.expiresAtMs > Date.now() + 300000) throw new Error('Scoped sign-in refused');
    applyBodyToken(result.token, result.user);
    return result.redirectPath;
  }, [location.pathname, location.search, location.hash, applyBodyToken]);

  const logout = useCallback(() => {
    // A test sign-in held by this tab only signs this tab out.
    const tabOnly = !!currentToken.current && tabToken() === currentToken.current;
    currentToken.current = null;
    setUser(null);
    setToken(null);
    setLoading(false);
    setAuthError(null);
    setTabToken(null);
    if (!tabOnly) localStorage.removeItem('token');
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    if (!incomingToken) return;
    loginWithToken(incomingToken);
    const query = new URLSearchParams(location.search);
    const fragment = new URLSearchParams(location.hash.slice(1));
    query.delete('token');
    const hadFragmentToken = location.hash.startsWith('#token=');
    fragment.delete('token');
    navigate({
      pathname: location.pathname,
      search: query.toString() ? `?${query}` : '',
      hash: hadFragmentToken ? (fragment.toString() ? `#${fragment}` : '') : location.hash,
    }, { replace: true });
    // No reload: LockDown Browser can refuse it. Authentication state itself
    // must consume the token before a protected route decides to show login.
  }, [incomingToken, location.pathname, location.search, location.hash, navigate, loginWithToken]);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoading(false);
      return undefined;
    }
    if (currentToken.current !== token) return undefined;
    if (tabToken() !== token) localStorage.setItem('token', token);
    authAPI.getProfile(token).then((response) => {
      if (cancelled || currentToken.current !== token) return;
      setUser(response.user || response);
      setLoading(false);
    }).catch(() => {
      if (cancelled || currentToken.current !== token) return;
      logout();
      setAuthError('Your sign-in could not be completed. Return to D2L and open the lab link again.');
    });
    return () => { cancelled = true; };
  }, [token, logout]);

  useEffect(() => {
    const changed = (event) => {
      if (event.key !== 'token') return;
      // This tab is signed in as a test student on its own.
      if (tabToken() && tabToken() === currentToken.current) return;
      if (event.newValue && isCanaryToken(event.newValue)) return;
      if (event.newValue) loginWithToken(event.newValue);
      else logout();
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, [loginWithToken, logout]);

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Password change failed' 
      };
    }
  };

  const value = {
    user,
    token,
    loading: loading || !!(incomingToken && incomingToken !== token),
    authError,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isFaculty: user?.role === 'faculty' || user?.role === 'admin',
    isStudent: user?.role === 'student',
    loginWithToken,
    beginCanarySignIn,
    completeCanarySignIn,
    logout,
    changePassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
